import React, { useState, useCallback } from "react";
import axios from 'axios';
import styles from './MobileTrendCard.module.css';
import upTrendIcon from '../../assets/image/dashboard-images/uptrend-icon2.png';
import downTrendIcon from '../../assets/image/dashboard-images/down-arrow 1.png';
import watchlist from '../../assets/image/add.png';
import graph from '../../assets/image/chart.png';
import feedback from '../../assets/image/feedback.png';
import TrendChart from '../../components/chart/TrendChart';
import MobileFeedbackWatchlistActions from '../../components/common/MobileFeedbackWatchlistActions';
import { API } from '../../config/api';
import { FaCircleChevronDown } from "react-icons/fa6";

const MobileTrendCard = ({ item, trendType }) => {

    const [showChart, setShowChart] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);



    let logoSrc;

    try {
        logoSrc = require(`../../assets/image/stock-logo/${item.stock}.png`);
    } catch (err) {
        logoSrc = require('../../assets/image/stock-logo/default.png'); // fallback image
    }

    // Map trendType to timeframe
    const getTimeframe = (trendType) => {
        const timeframeMap = {
            'D': 'DAY',
            'W': 'WEEK',
            'M': 'MONTH'
        };
        return timeframeMap[trendType] || 'DAY';
    };

    // API call to fetch chart data
    const fetchChartData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(API.getChartTrends, {
                params: {
                    stockUid: item.stockUid,
                    timeframe: getTimeframe(trendType)
                }
            });

            // Destructure the nested data property
            const { data: chartApiData } = response.data;

            setChartData(chartApiData);
            setShowChart(true);
        } catch (err) {
            console.error('Error fetching chart data:', err);
            setError('Failed to load chart data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [item.stockUid, trendType]);

    const getTrend = (item, trendType) => {
        return trendType === "D" ? item.day.trend : trendType === "W" ? item.week.trend : item.month.trend;
    };

    const getTrendIcon = (item, trendType) => {
        return getTrend(item, trendType) === "UP" ? <img src={upTrendIcon} alt="Uptrend" /> : <img src={downTrendIcon} alt="Downtrend" />;
    };

    const getTrendText = (item, trendType) => {
        return getTrend(item, trendType) === "UP" ? "In Uptrend" : "In Downtrend";
    };

    const getTrendNextLevelText = (item, trendType) => {
        return getTrend(item, trendType) === "UP" ? "Next Resistance" : "Next Support";
    };

    const getTrendNextLevelValue = (item, trendType, levelType = null) => {
        let trend = getTrend(item, trendType);
        let nextLevelValue;

        if (levelType) {
            // For yAxisAnnotations, get both support and resistance
            if (levelType === "support") {
                nextLevelValue = trendType === "D" ? item.day.support : trendType === "W" ? item.week.support : item.month.support;
            } else {
                nextLevelValue = trendType === "D" ? item.day.resistance : trendType === "W" ? item.week.resistance : item.month.resistance;
            }
        } else {
            // Original logic for display
            if (trend === "UP") {
                nextLevelValue = trendType === "D" ? item.day.resistance : trendType === "W" ? item.week.resistance : item.month.resistance;
            } else {
                nextLevelValue = trendType === "D" ? item.day.support : trendType === "W" ? item.week.support : item.month.support;
            }
        }

        return nextLevelValue;
    };


    const handleCardClick = () => {
        fetchChartData();
    };

    const handleCloseChart = useCallback(() => {
        setShowChart(false);
        setChartData(null);
        setError(null);
    }, []);

    return (
        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12">
            <div className={styles.trends_card_wrapper}>
                <div
                    className={styles.trends_card_top}
                    onClick={handleCardClick}
                    style={{ cursor: loading ? 'wait' : 'pointer' }}
                >
                    <div className={styles.trends_name_section}>
                        <div className={styles.trends_logo_wrapper}>
                            <img src={logoSrc} alt={`${item.stock} logo`} />
                        </div>
                        <h4>{item.stock}</h4>
                    </div>
                    <span>
                        <img className={styles.graph_icon} src={graph} alt="Launch Graph" />
                    </span>
                </div>
                <div className={styles.trends_card_body}>
                    <div className={styles.trend_section}>
                        <>{getTrendIcon(item, trendType)}</>
                        <p>{getTrendText(item, trendType)}</p>
                    </div>
                    <div className={styles.info_section}>
                        <div className={styles.value_wrapper}>
                            <p>LTP</p>
                            <h5>{item.ltp}</h5>
                        </div>
                        <div className={styles.value_wrapper}>
                            <p>{getTrendNextLevelText(item, trendType)}</p>
                            <h5>{getTrendNextLevelValue(item, trendType)}</h5>
                        </div>
                    </div>
                </div>
                <div className={styles.trends_card_footer}>
                    <div></div>
                    <MobileFeedbackWatchlistActions stockData={item} styles={styles} referenceType="trends" referenceUId={item.trendUid} />
                    <p><FaCircleChevronDown /></p>
                </div>
            </div>

            {loading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
                    <div className="bg-white p-4 rounded shadow-lg text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="mb-0">Loading Chart Data...</h5>
                    </div>
                </div>
            )}

            {error && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
                    <div className="bg-white p-4 rounded shadow-lg text-center">
                        <div className="text-danger mb-3">
                            <i className="fas fa-exclamation-triangle fa-3x"></i>
                        </div>
                        <h5 className="text-danger mb-3">Error</h5>
                        <p className="mb-3">{error}</p>
                        <div className="d-flex gap-2 justify-content-center">
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={fetchChartData}
                            >
                                Retry
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleCloseChart}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showChart && chartData && (
                <TrendChart
                    data={chartData}
                    logoSrc={logoSrc}
                    onClose={handleCloseChart}
                />
            )}


        </div>
    )
}

export default MobileTrendCard